import { BaseEdge, EdgeLabelRenderer, MarkerType, type Position, getSmoothStepPath } from '@xyflow/react';
import { useState } from 'react';
import { css } from 'aphrodite';
import { type FlowContact } from '../types';
import { ContactFlowVisualizer } from '../components/ContactFlowVisualizer';
import { formatFilterCondition, formatFilterForNode } from '../utils/filterDisplay';
import { type BranchEdgeData } from '../types/edges';
import { BRANCH_COLORS, branchEdgeStyles } from './branchEdgeStyles';
import { charmGray } from '../../../styles/colors';

interface BranchEdgeProps {
	id: string;
	sourceX: number;
	sourceY: number;
	targetX: number;
	targetY: number;
	sourcePosition: Position;
	targetPosition: Position;
	selected?: boolean;
	data?: BranchEdgeData & {
		activeContacts?: FlowContact[];
	};
	source: string;
	target: string;
}

export function BranchEdge(props: BranchEdgeProps) {
	const {
		id,
		sourceX,
		sourceY,
		targetX,
		targetY,
		sourcePosition,
		targetPosition,
		selected,
		data,
		source: _source,
		target: _target,
	} = props;

	const [showTooltip, setShowTooltip] = useState(false);

	const activeContacts = data?.activeContacts || [];
	const caseIndex = data?.caseIndex ?? 0;
	const _totalCases = data?.totalCases ?? 1;
	const isDefault = data?.isDefault ?? false;
	const condition = data?.condition;
	const caseName = data?.caseName;

	// Use caseColor from data if available, otherwise fall back to calculated color
	const branchColor = data?.caseColor || (isDefault ? charmGray : BRANCH_COLORS[caseIndex % BRANCH_COLORS.length]);

	const [edgePath] = getSmoothStepPath({
		sourceX,
		sourceY,
		sourcePosition,
		targetX,
		targetY,
		targetPosition,
		borderRadius: 8,
	});

	// Position label on the vertical segment near the target (case) node
	// Place it on the straight vertical line right before entering the target
	const VERTICAL_OFFSET_FROM_TARGET = 60;
	const labelX = targetX;
	const labelY = targetY - VERTICAL_OFFSET_FROM_TARGET;

	// Prioritize showing filter condition on the edge label
	// Show filter condition if available, otherwise fall back to case name or default text
	let labelText: string;
	if (isDefault) {
		labelText = 'Default';
	} else if (condition) {
		// Always show filter condition for non-default cases
		labelText = formatFilterCondition(condition);
	} else if (caseName) {
		// Fallback to case name if no condition
		labelText = caseName;
	} else {
		// Final fallback
		labelText = `Case ${caseIndex + 1}`;
	}

	const edgeAriaLabel = `Branch connection: ${labelText}${activeContacts.length > 0 ? `, ${activeContacts.length} contacts taking this path` : ''}`;

	return (
		<>
			<g role='img' aria-label={edgeAriaLabel}>
				<BaseEdge
					id={id}
					path={edgePath}
					markerEnd={MarkerType.ArrowClosed}
					style={{
						stroke: selected ? branchColor : branchColor + 'CC',
						strokeWidth: selected ? 3 : 2,
						strokeDasharray: isDefault ? '5,5' : 'none',
					}}
				/>
			</g>
			<EdgeLabelRenderer>
				<div
					className={css(branchEdgeStyles.labelContainer)}
					style={{
						transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
					}}
					onMouseEnter={() => setShowTooltip(true)}
					onMouseLeave={() => setShowTooltip(false)}
				>
					<div
						className={css(branchEdgeStyles.labelBadge)}
						style={{
							backgroundColor: branchColor,
						}}
						title={labelText}
						role='status'
						aria-label={`Branch condition: ${labelText}`}
					>
						{labelText}
					</div>
					{showTooltip && condition && !isDefault && (
						<div
							className={css(branchEdgeStyles.tooltip)}
							style={{
								transform: 'translateX(-50%)',
							}}
							role='tooltip'
							aria-live='polite'
						>
							<div className={css(branchEdgeStyles.tooltipHeader)}>Full Condition:</div>
							{formatFilterForNode(condition)}
						</div>
					)}
				</div>
			</EdgeLabelRenderer>
			{activeContacts.length > 0 && (
				<ContactFlowVisualizer
					edgeId={id}
					sourceX={sourceX}
					sourceY={sourceY}
					targetX={targetX}
					targetY={targetY}
					sourcePosition={sourcePosition}
					targetPosition={targetPosition}
					activeContacts={activeContacts}
				/>
			)}
		</>
	);
}
